import { createVectorDB } from '@neureus/vector-db';
import type { VectorDB, VectorSearchResult } from '@neureus/vector-db';
import type {
  Chunk,
  ContextResult,
  QueryRequest,
  RAGEnv,
  DocumentMetadata,
} from '../types';
import { RetrievalError } from '../types';
import { EmbeddingService } from '../processing/embedding-service';
import { 
  cosineSimilarity,
  textSimilarity,
  extractKeywords,
  normalizeQuery,
  estimateTokens,
} from '../core/utils';

/**
 * Advanced retrieval system with hybrid search capabilities
 */
export class HybridRetriever {
  private vectorDB: VectorDB;
  private embeddingService: EmbeddingService;
  private env: RAGEnv;

  constructor(env: RAGEnv, embeddingService: EmbeddingService) {
    this.env = env;
    this.embeddingService = embeddingService;
    this.vectorDB = createVectorDB(env, {
      cache: {
        enabled: true,
        ttl: 3600,
        maxSize: 10000,
        strategy: 'lru',
      },
      maxBatchSize: 1000,
      defaultTopK: 50,
      maxTopK: 100,
      indexConfig: {
        type: 'hnsw',
        metric: 'cosine',
        buildThreshold: 100,
      },
      analytics: {
        enabled: true,
        sampleRate: 0.1,
      },
    });
  }

  /**
   * Initialize the retriever
   */
  async initialize(): Promise<void> {
    await this.vectorDB.initialize();
  }

  /**
   * Retrieve relevant context for a query using hybrid search
   */
  async retrieveContext(
    query: string,
    options: {
      topK?: number;
      minSimilarity?: number;
      hybridWeight?: number;
      rerankResults?: boolean;
      maxContextTokens?: number;
      filter?: Record<string, any>;
      namespace?: string;
      includeMetadata?: boolean;
    } = {}
  ): Promise<ContextResult> {
    const startTime = Date.now();
    
    try {
      const {
        topK = 5,
        minSimilarity = 0.7,
        hybridWeight = 0.7,
        rerankResults = true,
        maxContextTokens = 4000,
        filter,
        namespace,
        includeMetadata = true,
      } = options;

      // Step 1: Generate query embedding
      const queryEmbedding = await this.embeddingService.generateQueryEmbedding(query);

      // Step 2: Vector search
      const vectorResults = await this.vectorSearch(queryEmbedding, {
        topK: topK * 2, // Get more results for reranking
        minSimilarity,
        filter,
        namespace,
        includeMetadata,
      });

      // Step 3: Keyword search
      const keywordResults = await this.keywordSearch(query, {
        topK: topK * 2,
        filter,
        namespace,
        includeMetadata,
      });

      // Step 4: Hybrid fusion
      const hybridResults = this.fuseResults(
        vectorResults,
        keywordResults,
        hybridWeight,
        query
      );

      // Step 5: Rerank if requested
      const finalResults = rerankResults 
        ? await this.rerankResults(hybridResults, query, queryEmbedding)
        : hybridResults;

      // Step 6: Select top results and build context
      const topResults = finalResults.slice(0, topK);
      const context = await this.buildContext(topResults, maxContextTokens);

      const retrievalTime = Date.now() - startTime;

      return {
        chunks: context.chunks,
        sources: context.sources,
        totalTokens: context.totalTokens,
        retrievalTime,
        relevanceScores: topResults.map(r => r.score),
      };
    } catch (error) {
      throw new RetrievalError(
        `Context retrieval failed: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Perform vector similarity search
   */
  private async vectorSearch(
    queryEmbedding: number[],
    options: {
      topK: number;
      minSimilarity: number;
      filter?: Record<string, any>;
      namespace?: string;
      includeMetadata?: boolean;
    }
  ): Promise<VectorSearchResult[]> {
    const searchResponse = await this.vectorDB.search({
      vector: queryEmbedding,
      topK: options.topK,
      filter: options.filter,
      namespace: options.namespace,
      includeMetadata: options.includeMetadata || true,
      includeValues: false,
      minSimilarity: options.minSimilarity,
    });

    return searchResponse.matches;
  }

  /**
   * Perform keyword-based search
   */
  private async keywordSearch(
    query: string,
    options: {
      topK: number;
      filter?: Record<string, any>;
      namespace?: string;
      includeMetadata?: boolean;
    }
  ): Promise<VectorSearchResult[]> {
    try {
      // Get all chunks from the namespace (this could be optimized with a proper full-text search index)
      const allChunks = await this.getAllChunks(options.namespace, options.filter);
      
      // Normalize query for better matching
      const normalizedQuery = normalizeQuery(query);
      const queryKeywords = extractKeywords(normalizedQuery, 2, 10);
      
      // Score chunks based on keyword overlap and text similarity
      const scoredChunks = allChunks
        .map(chunk => {
          const content = chunk.metadata?.content || '';
          const normalizedContent = normalizeQuery(content);
          
          // Calculate keyword overlap score
          const contentKeywords = extractKeywords(normalizedContent, 2, 50);
          const keywordOverlap = queryKeywords.filter(kw => 
            contentKeywords.some(ckw => ckw.includes(kw) || kw.includes(ckw))
          ).length;
          const keywordScore = keywordOverlap / Math.max(queryKeywords.length, 1);
          
          // Calculate text similarity score
          const textSim = textSimilarity(normalizedQuery, normalizedContent);
          
          // Combine scores
          const combinedScore = (keywordScore * 0.6) + (textSim * 0.4);
          
          return {
            id: chunk.id,
            score: Math.min(combinedScore, 0.99), // Cap at 0.99 to distinguish from vector scores
            metadata: chunk.metadata,
          };
        })
        .filter(result => result.score > 0.1) // Filter out very low scores
        .sort((a, b) => b.score - a.score)
        .slice(0, options.topK);
      
      return scoredChunks;
    } catch (error) {
      console.warn('Keyword search failed, falling back to vector search only:', error);
      return [];
    }
  }

  /**
   * Get all chunks from a namespace (placeholder implementation)
   */
  private async getAllChunks(
    namespace?: string,
    filter?: Record<string, any>
  ): Promise<Array<{ id: string; metadata: any }>> {
    // This is a simplified implementation
    // In production, you'd want a proper full-text search index
    
    try {
      // Query the D1 database for chunk metadata
      const query = `
        SELECT chunk_id, content, metadata 
        FROM chunks 
        WHERE namespace = ? OR ? IS NULL
        ${filter ? 'AND json_extract(metadata, ?) IS NOT NULL' : ''}
        LIMIT 1000
      `;
      
      const params = [namespace, namespace];
      if (filter) {
        params.push(`$.${Object.keys(filter)[0]}`);
      }
      
      const result = await this.env.VECTOR_DB.prepare(query).bind(...params).all();
      
      return result.results?.map((row: any) => ({
        id: row.chunk_id,
        metadata: {
          content: row.content,
          ...JSON.parse(row.metadata || '{}'),
        },
      })) || [];
    } catch (error) {
      console.warn('Failed to get chunks from database:', error);
      return [];
    }
  }

  /**
   * Fuse vector and keyword search results using hybrid scoring
   */
  private fuseResults(
    vectorResults: VectorSearchResult[],
    keywordResults: VectorSearchResult[],
    hybridWeight: number,
    query: string
  ): Array<VectorSearchResult & { fusedScore: number }> {
    const resultMap = new Map<string, VectorSearchResult & { fusedScore: number }>();
    
    // Add vector results with weighted scores
    vectorResults.forEach((result, index) => {
      const normalizedScore = result.score * hybridWeight;
      const rankBoost = (vectorResults.length - index) / vectorResults.length * 0.1;
      
      resultMap.set(result.id, {
        ...result,
        fusedScore: normalizedScore + rankBoost,
      });
    });
    
    // Add or enhance with keyword results
    keywordResults.forEach((result, index) => {
      const keywordScore = result.score * (1 - hybridWeight);
      const rankBoost = (keywordResults.length - index) / keywordResults.length * 0.1;
      
      const existing = resultMap.get(result.id);
      if (existing) {
        // Boost existing result with keyword score
        existing.fusedScore += keywordScore + rankBoost;
      } else {
        // Add new result
        resultMap.set(result.id, {
          ...result,
          fusedScore: keywordScore + rankBoost,
        });
      }
    });
    
    // Sort by fused score
    return Array.from(resultMap.values()).sort((a, b) => b.fusedScore - a.fusedScore);
  }

  /**
   * Rerank results using advanced scoring
   */
  private async rerankResults(
    results: Array<VectorSearchResult & { fusedScore: number }>,
    query: string,
    queryEmbedding: number[]
  ): Promise<Array<VectorSearchResult & { fusedScore: number }>> {
    // This is a simplified reranking implementation
    // In production, you might use a dedicated reranking model
    
    return results.map(result => {
      let rerankScore = result.fusedScore;
      
      // Boost based on metadata relevance
      if (result.metadata) {
        // Boost if title matches query
        if (result.metadata.title) {
          const titleSimilarity = textSimilarity(
            normalizeQuery(query),
            normalizeQuery(result.metadata.title)
          );
          rerankScore += titleSimilarity * 0.2;
        }
        
        // Boost recent content
        if (result.metadata.updatedAt) {
          const age = Date.now() - result.metadata.updatedAt;
          const ageInDays = age / (1000 * 60 * 60 * 24);
          if (ageInDays < 30) {
            rerankScore += 0.05; // Small boost for recent content
          }
        }
        
        // Boost based on document type or tags
        if (result.metadata.tags?.includes('important') || 
            result.metadata.tags?.includes('featured')) {
          rerankScore += 0.1;
        }
      }
      
      return {
        ...result,
        fusedScore: Math.min(rerankScore, 1.0), // Cap at 1.0
      };
    }).sort((a, b) => b.fusedScore - a.fusedScore);
  }

  /**
   * Build context from selected chunks
   */
  private async buildContext(
    results: VectorSearchResult[],
    maxTokens: number
  ): Promise<{
    chunks: Chunk[];
    sources: DocumentMetadata[];
    totalTokens: number;
  }> {
    const chunks: Chunk[] = [];
    const sourceIds = new Set<string>();
    const sources: DocumentMetadata[] = [];
    let totalTokens = 0;
    
    for (const result of results) {
      // Get full chunk data
      const chunk = await this.getChunkById(result.id);
      if (!chunk) continue;
      
      const chunkTokens = estimateTokens(chunk.content);
      if (totalTokens + chunkTokens > maxTokens) {
        break;
      }
      
      chunks.push(chunk);
      totalTokens += chunkTokens;
      
      // Track unique sources
      const documentId = chunk.metadata.documentId;
      if (!sourceIds.has(documentId)) {
        sourceIds.add(documentId);
        const source = await this.getDocumentMetadata(documentId);
        if (source) {
          sources.push(source);
        }
      }
    }
    
    return {
      chunks,
      sources,
      totalTokens,
    };
  }

  /**
   * Get chunk by ID from storage
   */
  private async getChunkById(chunkId: string): Promise<Chunk | null> {
    try {
      // First try to get from vector database
      const vectorEntry = await this.vectorDB.get(chunkId);
      if (vectorEntry) {
        return {
          id: vectorEntry.id,
          content: vectorEntry.metadata?.content || '',
          tokens: estimateTokens(vectorEntry.metadata?.content || ''),
          embedding: vectorEntry.vector,
          metadata: vectorEntry.metadata as any,
        };
      }
      
      // Fallback to D1 database
      const result = await this.env.VECTOR_DB
        .prepare('SELECT * FROM chunks WHERE chunk_id = ?')
        .bind(chunkId)
        .first();
      
      if (!result) return null;
      
      return {
        id: result.chunk_id as string,
        content: result.content as string,
        tokens: estimateTokens(result.content as string),
        metadata: JSON.parse(result.metadata as string || '{}'),
      };
    } catch (error) {
      console.warn(`Failed to get chunk ${chunkId}:`, error);
      return null;
    }
  }

  /**
   * Get document metadata by ID
   */
  private async getDocumentMetadata(documentId: string): Promise<DocumentMetadata | null> {
    try {
      const result = await this.env.VECTOR_DB
        .prepare('SELECT * FROM documents WHERE document_id = ?')
        .bind(documentId)
        .first();
      
      if (!result) return null;
      
      return JSON.parse(result.metadata as string);
    } catch (error) {
      console.warn(`Failed to get document metadata ${documentId}:`, error);
      return null;
    }
  }

  /**
   * Store chunks in the vector database
   */
  async storeChunks(chunks: Chunk[], namespace?: string): Promise<void> {
    try {
      // Generate embeddings for chunks
      const embeddings = await this.embeddingService.generateEmbeddings(chunks);
      
      // Create vector entries
      const vectorEntries = chunks.map((chunk, index) => ({
        id: chunk.id,
        vector: embeddings[index],
        metadata: {
          content: chunk.content,
          ...chunk.metadata,
        },
        timestamp: Date.now(),
      }));
      
      // Store in vector database
      await this.vectorDB.upsertBatch({
        vectors: vectorEntries,
        namespace,
      });
      
      // Also store in D1 for metadata queries
      await this.storeChunksInD1(chunks, namespace);
      
    } catch (error) {
      throw new RetrievalError(
        `Failed to store chunks: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Store chunks in D1 database for metadata queries
   */
  private async storeChunksInD1(chunks: Chunk[], namespace?: string): Promise<void> {
    const stmt = this.env.VECTOR_DB.prepare(`
      INSERT OR REPLACE INTO chunks (chunk_id, document_id, content, tokens, metadata, namespace, created_at)
      VALUES (?, ?, ?, ?, ?, ?, ?)
    `);
    
    for (const chunk of chunks) {
      await stmt.bind(
        chunk.id,
        chunk.metadata.documentId,
        chunk.content,
        chunk.tokens,
        JSON.stringify(chunk.metadata),
        namespace || 'default',
        Date.now()
      ).run();
    }
  }

  /**
   * Store document metadata
   */
  async storeDocumentMetadata(metadata: DocumentMetadata): Promise<void> {
    try {
      await this.env.VECTOR_DB
        .prepare(`
          INSERT OR REPLACE INTO documents (document_id, metadata, created_at, updated_at)
          VALUES (?, ?, ?, ?)
        `)
        .bind(
          metadata.id,
          JSON.stringify(metadata),
          metadata.createdAt || Date.now(),
          metadata.updatedAt || Date.now()
        )
        .run();
    } catch (error) {
      console.warn(`Failed to store document metadata ${metadata.id}:`, error);
    }
  }

  /**
   * Search for similar chunks (for deduplication)
   */
  async findSimilarChunks(
    chunk: Chunk,
    threshold: number = 0.95,
    namespace?: string
  ): Promise<Chunk[]> {
    try {
      if (!chunk.embedding) {
        chunk.embedding = await this.embeddingService.generateEmbedding(chunk);
      }
      
      const results = await this.vectorDB.search({
        vector: chunk.embedding,
        topK: 10,
        minSimilarity: threshold,
        namespace,
        includeMetadata: true,
        includeValues: true,
      });
      
      return results.matches
        .filter(match => match.id !== chunk.id) // Exclude self
        .map(match => ({
          id: match.id,
          content: match.metadata?.content || '',
          tokens: estimateTokens(match.metadata?.content || ''),
          embedding: match.vector,
          metadata: match.metadata as any,
        }));
    } catch (error) {
      console.warn('Similar chunk search failed:', error);
      return [];
    }
  }

  /**
   * Delete chunks by document ID
   */
  async deleteChunksByDocument(documentId: string, namespace?: string): Promise<number> {
    try {
      // Delete from D1 first to get count
      const deleteResult = await this.env.VECTOR_DB
        .prepare('DELETE FROM chunks WHERE document_id = ? AND (namespace = ? OR ? IS NULL)')
        .bind(documentId, namespace, namespace)
        .run();
      
      // Delete from vector database
      // (This would need to be implemented in the vector DB to support batch deletion by metadata)
      
      return deleteResult.changes || 0;
    } catch (error) {
      console.warn(`Failed to delete chunks for document ${documentId}:`, error);
      return 0;
    }
  }

  /**
   * Get retrieval statistics
   */
  async getStats(): Promise<{
    totalChunks: number;
    totalDocuments: number;
    namespaces: string[];
    indexStats: any;
  }> {
    try {
      const chunksResult = await this.env.VECTOR_DB
        .prepare('SELECT COUNT(*) as count FROM chunks')
        .first();
      
      const docsResult = await this.env.VECTOR_DB
        .prepare('SELECT COUNT(*) as count FROM documents')
        .first();
      
      const namespacesResult = await this.env.VECTOR_DB
        .prepare('SELECT DISTINCT namespace FROM chunks')
        .all();
      
      const indexStats = await this.vectorDB.getIndexStats();
      
      return {
        totalChunks: chunksResult?.count as number || 0,
        totalDocuments: docsResult?.count as number || 0,
        namespaces: namespacesResult.results?.map((row: any) => row.namespace) || [],
        indexStats,
      };
    } catch (error) {
      console.warn('Failed to get retrieval stats:', error);
      return {
        totalChunks: 0,
        totalDocuments: 0,
        namespaces: [],
        indexStats: null,
      };
    }
  }
}